import { useCallback } from "react";
import useSWRImmutable from "swr/immutable";
import useSWRMutation from "swr/mutation";
import { Paginated, PaginationQuery, UpdateOrderRequestBody, UpdateVisibilityRequestBody } from "../../types";
import { buildUrl, mutateCache, useApi } from "../api";
import { CustomError } from "../errors";
import { InsertToeBoardBody, ToeBoard, UpdateToeBoardBody } from "./types/toeBoard";

export const useToeBoardsApi = () => {
  const api = useApi();

  const getAdminToeBoard = useCallback(
    (toeBoardId: string, signal: AbortSignal) => {
      const url = `/admin/settings/border/toe_boards/${toeBoardId}`;
      return useSWRMutation<ToeBoard, CustomError, string>(url, (url) => api.get<ToeBoard>({ url, signal }));
    },
    [api],
  );

  const useAdminToeBoards = useCallback(
    (query: PaginationQuery, signal: AbortSignal) => {
      const url = buildUrl(`/admin/settings/border/toe_boards`, query);
      return useSWRImmutable<Paginated<ToeBoard>, CustomError, string>(url, (url) =>
        api.get<Paginated<ToeBoard>>({ url, signal }),
      );
    },
    [api],
  );

  const getAdminToeBoards = useCallback(
    (query: PaginationQuery, signal: AbortSignal) => {
      const url = buildUrl(`/admin/settings/border/toe_boards`, query);
      return api.get<Paginated<ToeBoard>>({ url, signal }).then((x) => x.items);
    },
    [api],
  );

  const insertAdminToeBoard = useCallback(
    async (body: InsertToeBoardBody): Promise<{ id: string }> => {
      const url = `/admin/settings/border/toe_boards`;
      const id = await api.post<{ id: string }, InsertToeBoardBody>(url, body);
      mutateCache([url, `/admin/settings/border/toe_board_extras`]);
      return id;
    },
    [api],
  );

  const updateAdminToeBoard = useCallback(
    async (toeBoardId: string, body: UpdateToeBoardBody): Promise<void> => {
      const url = `/admin/settings/border/toe_boards/${toeBoardId}`;
      await api.put<UpdateToeBoardBody>(url, body);
      mutateCache(["/admin/settings/border/toe_boards", `/admin/settings/border/toe_board_extras`]);
    },
    [api],
  );

  const updateAdminToeBoardVisibility = useCallback(
    async (toeBoardId: string, visibility: UpdateVisibilityRequestBody): Promise<void> => {
      const url = `/admin/settings/border/toe_boards/${toeBoardId}/visibility`;
      (await api.put<UpdateVisibilityRequestBody>(url, visibility),
        mutateCache(["/admin/settings/border/toe_boards", `/admin/settings/border/toe_board_extras`]));
    },
    [api],
  );

  const updateAdminToeBoardOrder = useCallback(
    async (toeBoardId: string, order: UpdateOrderRequestBody): Promise<void> => {
      const url = `/admin/settings/border/toe_boards/${toeBoardId}/order`;
      (await api.put<UpdateOrderRequestBody>(url, order),
        mutateCache(["/admin/settings/border/toe_boards", `/admin/settings/border/toe_board_extras`]));
    },
    [api],
  );

  const deleteAdminToeBoard = useCallback(
    async (toeBoard: ToeBoard): Promise<void> => {
      const url = `/admin/settings/border/toe_boards/${toeBoard.item.id}`;
      await api.del(url);
      mutateCache([`/admin/settings/border/toe_boards`, `/admin/settings/border/toe_board_extras`]);
    },
    [api],
  );

  return {
    getAdminToeBoard,
    insertAdminToeBoard,
    updateAdminToeBoard,
    updateAdminToeBoardVisibility,
    updateAdminToeBoardOrder,
    deleteAdminToeBoard,
    useAdminToeBoards,
    getAdminToeBoards,
  };
};
